import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import {
  useContractDetail,
  useContractTransitions,
  CONTRACT_STATUS_LABELS,
  type ContractStatus,
} from '@/hooks/useQuoteContract';
import {
  QUOTE_CONTRACT_PERMISSIONS,
  canSeeQuoteContractMenu,
  hasPermission,
} from '@/lib/quoteContractPermissions';

const BADGE: Record<ContractStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  signed: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  change_pending: 'bg-amber-100 text-amber-800',
  voided: 'bg-zinc-200 text-zinc-700',
};

export default function ContractDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id!;
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const canRead = canSeeQuoteContractMenu(permissions);
  const canContract = hasPermission(
    permissions,
    QUOTE_CONTRACT_PERMISSIONS.CONTRACT,
  );
  const canChangeApprove = hasPermission(
    permissions,
    QUOTE_CONTRACT_PERMISSIONS.CHANGE_APPROVE,
  );

  const { contract, loading, error, fetchContract, setContract } =
    useContractDetail();
  const transitions = useContractTransitions();

  const [representative, setRepresentative] = useState('');
  const [voidReason, setVoidReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (canRead) void fetchContract(id);
  }, [canRead, id, fetchContract]);

  if (!canRead) return <AccessDenied resource="合同" />;
  if (loading && !contract) {
    return <Card className="m-6 p-6">加载中…</Card>;
  }
  if (error) {
    return (
      <Card className="m-6 bg-destructive/10 p-6 text-sm text-destructive">
        加载失败：{error.message}
      </Card>
    );
  }
  if (!contract) return null;

  const refresh = (next: typeof contract) => {
    if (next) setContract(next);
    void fetchContract(id);
  };

  const handleSign = async () => {
    setActionError(null);
    if (!representative.trim()) {
      setActionError('签署人必填');
      return;
    }
    const next = await transitions.sign(id, {
      expectedVersion: contract.version,
      signedRepresentative: representative,
    });
    if (next) refresh(next);
    else if (transitions.error) setActionError(transitions.error.message);
  };

  const handleActivate = async () => {
    setActionError(null);
    const next = await transitions.activate(id, {
      expectedVersion: contract.version,
    });
    if (next) refresh(next);
    else if (transitions.error) setActionError(transitions.error.message);
  };

  const handleVoid = async () => {
    setActionError(null);
    if (!voidReason.trim()) {
      setActionError('作废原因必填');
      return;
    }
    const next = await transitions.voidContract(id, {
      expectedVersion: contract.version,
      reason: voidReason,
    });
    if (next) refresh(next);
    else if (transitions.error) setActionError(transitions.error.message);
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-semibold">{contract.title}</h1>
          <p className="text-sm text-muted-foreground">
            合同号 {contract.contractNo} · 客户 {contract.customerId} · 报价{' '}
            {contract.quoteId}
          </p>
        </div>
        <span
          className={`ml-auto rounded-full px-3 py-0.5 text-xs ${BADGE[contract.status]}`}
        >
          {CONTRACT_STATUS_LABELS[contract.status]}
        </span>
      </div>

      {actionError && (
        <Card className="bg-destructive/10 p-3 text-sm text-destructive">
          {actionError}
        </Card>
      )}

      <Card className="space-y-2 p-4 text-sm">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <div>
            <div className="text-xs text-muted-foreground">币种</div>
            <div className="font-mono">{contract.currency}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">合同原始金额</div>
            <div className="font-mono">{contract.totalAmount}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">当前生效金额</div>
            <div className="font-mono font-semibold">
              {contract.effectiveTotalAmount}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">版本</div>
            <div className="text-xs">v{contract.version}</div>
          </div>
          {contract.signedAt && (
            <div>
              <div className="text-xs text-muted-foreground">签署时间</div>
              <div className="text-xs">
                {new Date(contract.signedAt).toLocaleString('zh-CN')}
              </div>
            </div>
          )}
          {contract.signedRepresentative && (
            <div>
              <div className="text-xs text-muted-foreground">签署代表</div>
              <div className="text-xs">{contract.signedRepresentative}</div>
            </div>
          )}
          {contract.voidedAt && (
            <div>
              <div className="text-xs text-muted-foreground">作废时间</div>
              <div className="text-xs">
                {new Date(contract.voidedAt).toLocaleString('zh-CN')}
              </div>
            </div>
          )}
        </div>
        {contract.voidReason && (
          <div className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
            作废原因：{contract.voidReason}
          </div>
        )}
      </Card>

      <Card className="space-y-2 p-4">
        <h3 className="font-medium">合同正文 (快照)</h3>
        <pre className="max-h-96 overflow-auto rounded-md bg-muted/30 p-3 text-xs">
          {contract.contentSnapshot}
        </pre>
      </Card>

      {canContract && (
        <Card className="space-y-3 p-4">
          <h3 className="font-medium">合同操作</h3>
          {contract.status === 'draft' && (
            <div className="flex items-center gap-2">
              <Input
                placeholder="签署代表姓名"
                value={representative}
                onChange={(e) => setRepresentative(e.target.value)}
              />
              <Button
                size="sm"
                onClick={handleSign}
                disabled={transitions.loading}
              >
                标记已签署
              </Button>
            </div>
          )}
          {contract.status === 'signed' && (
            <Button
              size="sm"
              onClick={handleActivate}
              disabled={transitions.loading}
            >
              标记生效
            </Button>
          )}
          {(contract.status === 'draft' ||
            contract.status === 'signed' ||
            contract.status === 'active' ||
            contract.status === 'change_pending') && (
            <div className="flex items-center gap-2">
              <Input
                placeholder="作废原因"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
              />
              <Button
                size="sm"
                variant="destructive"
                onClick={handleVoid}
                disabled={transitions.loading}
              >
                作废
              </Button>
            </div>
          )}
          {contract.status === 'voided' && (
            <p className="text-xs text-muted-foreground">合同已作废</p>
          )}
        </Card>
      )}

      {canChangeApprove &&
        (contract.status === 'signed' ||
          contract.status === 'active' ||
          contract.status === 'change_pending') && (
          <Card className="space-y-2 p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">报价变更</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  navigate(
                    `/quote-contract/change-requests/create?contractId=${encodeURIComponent(contract.id)}`,
                  )
                }
              >
                发起变更
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              已签署 / 生效中的合同可发起报价变更，审批通过后只更新生效金额，原始合同金额保留为历史快照。
            </p>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                navigate(
                  `/quote-contract/change-requests?contractId=${encodeURIComponent(contract.id)}`,
                )
              }
            >
              查看该合同的所有变更
            </Button>
          </Card>
        )}
    </div>
  );
}
